import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { Button, IconButton, Input, Select, Switch, Textarea } from '@/components/ui'
import { cn } from '@/lib/utils.ts'
import type {
  TemplateStep,
  StepConfig,
  StepType,
  StepExecutor,
  GatewayType,
  AssignmentType,
  TemplateVariable,
} from './types.ts'
import { STEP_TYPE_META } from './stepTypeMeta.ts'
import { FieldsManager } from './FieldsManager.tsx'

const STEP_TYPE_OPTIONS: { value: StepType; label: string }[] = [
  { value: 'task', label: 'Aufgabe' },
  { value: 'approval', label: 'Freigabe-Gate' },
  { value: 'form', label: 'Formular' },
  { value: 'notification', label: 'Benachrichtigung' },
  { value: 'condition', label: 'Verzweigung (Gateway)' },
]

const TANSS_TYPE_OPTIONS = [
  { value: '', label: 'Kein TANSS-Typ' },
  { value: 'repair', label: 'Reparatur' },
  { value: 'maintenance', label: 'Wartung' },
  { value: 'installation', label: 'Installation' },
  { value: 'consulting', label: 'Beratung' },
  { value: 'other', label: 'Sonstiges' },
]

const TANSS_PRIORITY_OPTIONS = [
  { value: '', label: 'Keine Priorität' },
  { value: 'low', label: 'Niedrig' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Hoch' },
  { value: 'critical', label: 'Kritisch' },
]

const GATEWAY_OPTIONS: { value: GatewayType; label: string }[] = [
  { value: 'xor', label: 'XOR — genau ein Pfad' },
  { value: 'parallel_split', label: 'Parallel — alle Pfade gleichzeitig' },
  { value: 'parallel_join', label: 'Parallel-Join — wartet auf alle' },
  { value: 'inclusive', label: 'Inklusiv — alle zutreffenden' },
]

const ASSIGNMENT_OPTIONS: { value: AssignmentType | ''; label: string }[] = [
  { value: '', label: 'Keine feste Zuweisung' },
  { value: 'user', label: 'Bestimmte Person' },
  { value: 'role', label: 'Rolle / Abteilung' },
  { value: 'dynamic', label: 'Dynamisch aus Kontext' },
]

const ROLE_OPTIONS = [
  { value: 'technician', label: 'Technik' },
  { value: 'sales', label: 'Vertrieb' },
  { value: 'accounting', label: 'Buchhaltung' },
  { value: 'hr', label: 'HR' },
  { value: 'gf', label: 'Geschäftsführung' },
  { value: 'admin', label: 'Administration' },
]

const DYNAMIC_OPTIONS = [
  { value: 'starter', label: 'Starter des Prozesses' },
  { value: 'manager_of_subject', label: 'Vorgesetzter des Subjekts' },
  { value: 'account_manager_of_customer', label: 'Account-Manager des Kunden' },
]

type ActivateMode = 'none' | 'absolute' | 'relative'

/** Keeps executor in sync with the chosen step type (palette mapping). */
function stepTypeToExecutor(t: StepType): StepExecutor {
  if (t === 'approval') return 'approval'
  if (t === 'condition') return 'gateway'
  return 'manual'
}

interface StepInspectorProps {
  step: TemplateStep
  variables: TemplateVariable[]
  onClose: () => void
  onUpdate: (patch: Partial<TemplateStep & { config: Partial<StepConfig> }>) => void
  saving?: boolean
}

export function StepInspector({ step, variables, onClose, onUpdate, saving }: StepInspectorProps) {
  const [title, setTitle] = useState(step.title)
  const [description, setDescription] = useState(step.description ?? '')
  const [stepType, setStepType] = useState<StepType>(step.step_type)
  const [isRequired, setIsRequired] = useState(step.is_required === 1)
  const [tanssType, setTanssType] = useState(step.config?.tanss_type ?? '')
  const [tanssPriority, setTanssPriority] = useState(step.config?.tanss_priority ?? '')

  const [gatewayType, setGatewayType] = useState<GatewayType>(step.gateway_type ?? 'xor')
  const [assignmentType, setAssignmentType] = useState<AssignmentType | ''>(step.assignment_type ?? '')
  const [assignmentValue, setAssignmentValue] = useState(step.assignment_value ?? '')
  const [actionKey, setActionKey] = useState(step.action_key ?? '')
  const [subprocessTemplateId, setSubprocessTemplateId] = useState(step.subprocess_template_id ?? '')

  const rule = (step.activate_at_rule ?? {}) as { absolute?: string; field?: string; offsetDays?: number }
  const [activateMode, setActivateMode] = useState<ActivateMode>(
    rule.absolute ? 'absolute' : rule.field ? 'relative' : 'none',
  )
  const [activateAbsolute, setActivateAbsolute] = useState(rule.absolute ?? '')
  const [activateField, setActivateField] = useState(rule.field ?? '')
  const [activateOffset, setActivateOffset] = useState(String(rule.offsetDays ?? ''))

  const executor = stepTypeToExecutor(stepType)

  // Users for the "specific person" assignment.
  const { data: users } = useQuery<Array<{ id: string; name?: string; email?: string }>>({
    queryKey: ['users-min'],
    queryFn: () => apiClient.get('/users').then((r) => (Array.isArray(r.data) ? r.data : [])),
    staleTime: 5 * 60_000,
  })
  // Other templates for the sub-process selector.
  const { data: templates } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['prozesse-templates-min'],
    queryFn: () => apiClient.get('/prozesse/templates').then((r) => (Array.isArray(r.data) ? r.data : [])),
    staleTime: 60_000,
    enabled: executor === 'subprocess',
  })

  const dateVariables = variables.filter((v) => v.var_type === 'date' || v.var_type === 'datetime')

  useEffect(() => {
    setTitle(step.title)
    setDescription(step.description ?? '')
    setStepType(step.step_type)
    setIsRequired(step.is_required === 1)
    setTanssType(step.config?.tanss_type ?? '')
    setTanssPriority(step.config?.tanss_priority ?? '')
    setGatewayType(step.gateway_type ?? 'xor')
    setAssignmentType(step.assignment_type ?? '')
    setAssignmentValue(step.assignment_value ?? '')
    setActionKey(step.action_key ?? '')
    setSubprocessTemplateId(step.subprocess_template_id ?? '')
    const r = (step.activate_at_rule ?? {}) as { absolute?: string; field?: string; offsetDays?: number }
    setActivateMode(r.absolute ? 'absolute' : r.field ? 'relative' : 'none')
    setActivateAbsolute(r.absolute ?? '')
    setActivateField(r.field ?? '')
    setActivateOffset(String(r.offsetDays ?? ''))
  }, [step.id]) // eslint-disable-line react-hooks/exhaustive-deps

  function buildActivateRule(): Record<string, unknown> | null {
    if (activateMode === 'absolute' && activateAbsolute) return { absolute: activateAbsolute }
    if (activateMode === 'relative' && activateField) {
      const r: Record<string, unknown> = { field: activateField }
      if (activateOffset) r['offsetDays'] = Number(activateOffset)
      return r
    }
    return null
  }

  function handleSave() {
    const config: Partial<StepConfig> = {
      ...step.config,
      tanss_type: tanssType || undefined,
      tanss_priority: tanssPriority || undefined,
    }
    onUpdate({
      title: title.trim() || step.title,
      description: description.trim() || null,
      step_type: stepType,
      is_required: isRequired ? 1 : 0,
      config,
      executor,
      gateway_type: executor === 'gateway' ? gatewayType : null,
      assignment_type: executor === 'gateway' ? null : (assignmentType || null),
      assignment_value: executor === 'gateway' || !assignmentType ? null : (assignmentValue || null),
      action_key: executor === 'action' ? (actionKey || null) : null,
      subprocess_template_id: executor === 'subprocess' ? (subprocessTemplateId || null) : null,
      activate_at_rule: executor === 'gateway' ? null : buildActivateRule(),
    })
  }

  const meta = STEP_TYPE_META[step.step_type]
  const TypeIcon = meta.icon

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 shrink-0">
        <span className={cn('rounded p-1', meta.bgColor)}>
          <TypeIcon className={cn('w-4 h-4', meta.textColor)} />
        </span>
        <span className="text-sm font-semibold text-gray-900 flex-1 truncate">{step.title}</span>
        <IconButton icon={<X />} aria-label="Inspektor schließen" variant="ghost" size="sm" onClick={onClose} />
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        <Input label="Titel" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Schrittbezeichnung …" />
        <Textarea
          label="Beschreibung"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optionale Beschreibung …"
          rows={3}
        />
        <Select label="Typ" value={stepType} onValueChange={(v) => setStepType(v as StepType)} options={STEP_TYPE_OPTIONS} />

        <div className="flex items-center gap-3 py-1">
          <Switch checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} label="Pflichtschritt" />
          {isRequired && <span className="text-xs text-neutral-500">Kann nicht übersprungen werden</span>}
        </div>

        {/* Gateway type */}
        {executor === 'gateway' && (
          <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 space-y-3">
            <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Gateway</p>
            <Select
              label="Verzweigungstyp"
              value={gatewayType}
              onValueChange={(v) => setGatewayType(v as GatewayType)}
              options={GATEWAY_OPTIONS}
            />
            <p className="text-xs text-orange-700/80">
              Bedingungen werden an den ausgehenden Verbindungen gesetzt (Verbindung anklicken).
            </p>
          </div>
        )}

        {/* Assignment (non-gateway) */}
        {executor !== 'gateway' && (
          <div className="rounded-lg border border-gray-200 p-3 space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Zuweisung</p>
            <Select
              label="Verantwortlich"
              value={assignmentType}
              onValueChange={(v) => { setAssignmentType(v as AssignmentType | ''); setAssignmentValue('') }}
              options={ASSIGNMENT_OPTIONS}
            />
            {assignmentType === 'user' && (
              <Select
                label="Person"
                value={assignmentValue}
                onValueChange={setAssignmentValue}
                options={[
                  { value: '', label: 'Person wählen …' },
                  ...(users ?? []).map((u) => ({ value: u.id, label: u.name || u.email || u.id })),
                ]}
              />
            )}
            {assignmentType === 'role' && (
              <Select
                label="Rolle / Abteilung"
                value={assignmentValue}
                onValueChange={setAssignmentValue}
                options={[{ value: '', label: 'Rolle wählen …' }, ...ROLE_OPTIONS]}
              />
            )}
            {assignmentType === 'dynamic' && (
              <Select
                label="Regel"
                value={assignmentValue}
                onValueChange={setAssignmentValue}
                options={[{ value: '', label: 'Regel wählen …' }, ...DYNAMIC_OPTIONS]}
              />
            )}
          </div>
        )}

        {/* Time gating (non-gateway) */}
        {executor !== 'gateway' && (
          <div className="rounded-lg border border-gray-200 p-3 space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Zeitsteuerung</p>
            <Select
              label="Aktivierung"
              value={activateMode}
              onValueChange={(v) => setActivateMode(v as ActivateMode)}
              options={[
                { value: 'none', label: 'Sofort (sobald Vorbedingungen erfüllt)' },
                { value: 'absolute', label: 'An festem Datum' },
                { value: 'relative', label: 'Relativ zu einer Datums-Variable' },
              ]}
            />
            {activateMode === 'absolute' && (
              <Input type="date" label="Datum" value={activateAbsolute} onChange={(e) => setActivateAbsolute(e.target.value)} />
            )}
            {activateMode === 'relative' && (
              <>
                <Select
                  label="Datums-Variable"
                  value={activateField}
                  onValueChange={setActivateField}
                  options={[
                    { value: '', label: 'Variable wählen …' },
                    ...dateVariables.map((v) => ({ value: v.var_key, label: v.label })),
                    { value: 'subject.last_day', label: 'Subjekt: letzter Tag' },
                    { value: 'subject.start_date', label: 'Subjekt: Eintrittsdatum' },
                  ]}
                />
                <Input
                  type="number"
                  label="Versatz (Tage)"
                  value={activateOffset}
                  onChange={(e) => setActivateOffset(e.target.value)}
                  placeholder="z.B. -1 (Vortag) oder 0"
                  hint="Negative Werte = davor"
                />
              </>
            )}
          </div>
        )}

        {/* Form fields editor */}
        {stepType === 'form' && <FieldsManager templateId={step.template_id} stepId={step.id} />}

        {/* Task/form: TANSS mapping */}
        {(stepType === 'task' || stepType === 'form') && (
          <div className="rounded-lg border border-gray-200 p-3 space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">TANSS-Mapping</p>
            <Select label="TANSS Auftragstyp" value={tanssType} onValueChange={setTanssType} options={TANSS_TYPE_OPTIONS} />
            <Select label="TANSS Priorität" value={tanssPriority} onValueChange={setTanssPriority} options={TANSS_PRIORITY_OPTIONS} />
          </div>
        )}

        {/* Action */}
        {executor === 'action' && (
          <div className="rounded-lg border border-gray-200 p-3 space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Automatische Aktion</p>
            <Input
              label="Aktions-Schlüssel"
              value={actionKey}
              onChange={(e) => setActionKey(e.target.value)}
              placeholder="z.B. create_tanss_ticket"
              hint="Ohne registrierten Handler wird der Schritt manuell abgearbeitet."
            />
          </div>
        )}

        {/* Sub-process */}
        {executor === 'subprocess' && (
          <div className="rounded-lg border border-gray-200 p-3 space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Subprozess</p>
            <Select
              label="Zu startendes Template"
              value={subprocessTemplateId}
              onValueChange={setSubprocessTemplateId}
              options={[
                { value: '', label: 'Template wählen …' },
                ...(templates ?? []).filter((t) => t.id !== step.template_id).map((t) => ({ value: t.id, label: t.name })),
              ]}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>Abbrechen</Button>
        <Button variant="primary" size="sm" onClick={handleSave} loading={saving}>Speichern</Button>
      </div>
    </div>
  )
}
